import React, { useCallback, useEffect, useState } from 'react';
import './MaintenanceMode.css';

const listeners = new Set();
const HEALTH_POLL_MS = 5_000;

let maintenanceActive = false;

function emit(active) {
  maintenanceActive = Boolean(active);
  listeners.forEach((listener) => listener(maintenanceActive));
}

/** Flip the in-app maintenance overlay on/off. */
export function setMaintenanceMode(active) {
  if (Boolean(active) === maintenanceActive) return;
  emit(active);
}

export function isMaintenanceMode() {
  return maintenanceActive;
}

async function checkHealth() {
  try {
    const response = await fetch('/api/health', {
      method: 'GET',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (response.status === 503) {
      setMaintenanceMode(true);
      return false;
    }
    if (response.ok && maintenanceActive) {
      // Service is back — reload so the SPA reconnects cleanly.
      window.location.reload();
      return true;
    }
    if (response.ok) {
      setMaintenanceMode(false);
      return true;
    }
  } catch {
    // Network blip: don't flip into maintenance on a single failed probe.
  }
  return !maintenanceActive;
}

/**
 * Full-screen overlay when Render (or the API) is in maintenance / returning 503.
 * Also polls /api/health so idle tabs flip without waiting for a user action.
 */
export function MaintenanceModeHost() {
  const [active, setActive] = useState(maintenanceActive);

  const onChange = useCallback((next) => setActive(next), []);

  useEffect(() => {
    listeners.add(onChange);
    setActive(maintenanceActive);
    return () => {
      listeners.delete(onChange);
    };
  }, [onChange]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__setMaintenanceMode = setMaintenanceMode;

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await checkHealth();
    };
    tick();
    const id = window.setInterval(tick, HEALTH_POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      if (window.__setMaintenanceMode === setMaintenanceMode) {
        delete window.__setMaintenanceMode;
      }
    };
  }, []);

  if (!active) return null;

  return (
    <div
      className="maintenance-mode-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="maintenance-mode-title"
      aria-describedby="maintenance-mode-message"
    >
      <div className="maintenance-mode-card">
        <p className="maintenance-mode-eyebrow">Temporarily unavailable</p>
        <h1 id="maintenance-mode-title">We’ll be right back</h1>
        <p id="maintenance-mode-message">
          The site is in maintenance mode. This page will refresh automatically
          when service is restored.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export default MaintenanceModeHost;
